/**
 * 题目讲解播放页
 * 输入题目内容 → 调用 tutor.explainAsDeck → 使用 MicrocoursePlayer 播放
 * 支持 fragment 逐步显示 + TTS 自动朗读
 *
 * 缓存模式：带 ?questionId=xxx 进入时，优先读取 questions.auto_explanation
 * 中已缓存的讲解课件；缺失或无效时生成并通过 qb.update 回写。
 */

import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MicrocoursePlayer } from '@/components/microcourse'
import type { MicrocourseProject } from '@ai-tutor/shared'
import { explainAsDeck } from '@/api/tutor'
import {
  fetchQuestionDetail,
  updateQuestion,
  type AutoExplanation,
  type QuestionDraft,
} from '@/api/qb'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Loader2, Sparkles, RotateCcw, ImagePlus, RefreshCw } from 'lucide-react'

/** 校验缓存的讲解是否为可播放的 MicrocourseProject */
function isValidDeckExplanation(
  exp: AutoExplanation | null | undefined,
): exp is AutoExplanation {
  if (!exp) return false
  if (exp.format !== 'microcourse_deck') return false
  const slides = (exp.project as Record<string, unknown>)?.slides
  return Array.isArray(slides) && slides.length > 0
}

export default function QuestionPlayerPage() {
  const [searchParams] = useSearchParams()
  const questionId = searchParams.get('questionId')

  const [content, setContent] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [project, setProject] = useState<MicrocourseProject | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fromCache, setFromCache] = useState(false)
  // 缓存模式下的题目详情（重新生成时复用）
  const questionRef = useRef<QuestionDraft | null>(null)

  const canSubmit = (content.trim().length > 0 || imageUrl.trim().length > 0) && !loading

  /** 以题目详情为输入生成讲解，成功后回写缓存 */
  async function generateFromQuestion(detail: QuestionDraft) {
    const stem = detail.content?.stem ?? ''
    const image = detail.content?.images?.[0] || undefined
    const result = await explainAsDeck({ content: stem, image })
    setProject(result)
    setFromCache(false)

    if (questionId) {
      try {
        await updateQuestion(questionId, {
          autoExplanation: {
            format: 'microcourse_deck',
            version: 1,
            project: result as unknown as Record<string, unknown>,
          },
        })
      } catch (err) {
        console.warn('讲解缓存回写失败:', err)
      }
    }
  }

  // 缓存模式：挂载后读取题目详情
  useEffect(() => {
    if (!questionId) return
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      setProject(null)
      try {
        const detail = await fetchQuestionDetail(questionId!)
        if (cancelled) return
        questionRef.current = detail
        const exp = detail.autoExplanation ?? detail.auto_explanation
        if (isValidDeckExplanation(exp)) {
          setProject(exp.project as unknown as MicrocourseProject)
          setFromCache(true)
        } else {
          await generateFromQuestion(detail)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载题目失败')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId])

  /** 手动输入模式生成（不读不写缓存） */
  async function handleGenerate() {
    if (!canSubmit) return
    setLoading(true)
    setError(null)
    setProject(null)
    setFromCache(false)

    try {
      const result = await explainAsDeck({
        content: content.trim() || undefined,
        image: imageUrl.trim() || undefined,
      } as Parameters<typeof explainAsDeck>[0])
      setProject(result)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '生成失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  /** 缓存模式强制重新生成并覆盖回写 */
  async function handleRegenerate() {
    const detail = questionRef.current
    if (!detail || loading) return
    setLoading(true)
    setError(null)
    setProject(null)
    setFromCache(false)
    try {
      await generateFromQuestion(detail)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '生成失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  function handleReset() {
    setProject(null)
    setError(null)
    setFromCache(false)
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-800">题目讲解</h1>
        <div className="flex items-center gap-2">
          {project && questionId && (
            <Button variant="outline" size="sm" onClick={handleRegenerate} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-1" />
              重新生成
            </Button>
          )}
          {project && !questionId && (
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-1" />
              重新输入
            </Button>
          )}
        </div>
      </div>

      {/* Input area (manual mode only, hidden after successful generation) */}
      {!questionId && !project && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              题目内容
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="输入题目文本，例如：已知函数 f(x) = x² - 2x + 1，求 f(x) 的最小值。"
              rows={4}
              className="resize-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
              <ImagePlus className="w-3.5 h-3.5" />
              题目图片 URL（可选）
            </label>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/question.png"
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleGenerate}
              disabled={!canSubmit}
              className="gap-1.5"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {loading ? 'AI 正在生成讲解...' : '生成讲解'}
            </Button>
            <p className="text-xs text-slate-400">
              将逐步展示解题过程，支持语音朗读
            </p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm text-slate-500">
            AI 正在分析题目并生成讲解课件...
          </p>
          <p className="text-xs text-slate-400">
            包含解题步骤、语音朗读、要点总结
          </p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <p className="text-red-500 text-sm">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={questionId ? handleRegenerate : handleGenerate}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            重试
          </Button>
        </div>
      )}

      {/* Player */}
      {project && !loading && (
        <div className="space-y-2">
          {fromCache && (
            <p className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-1.5 inline-block">
              已使用缓存讲解，如需更新请点击「重新生成」
            </p>
          )}
          <MicrocoursePlayer
            project={project}
            showFullscreen
            className="min-h-[500px]"
          />
        </div>
      )}

      {/* Empty state (manual mode) */}
      {!questionId && !project && !loading && !error && (
        <div className="flex flex-col items-center justify-center h-48 text-slate-400 border border-dashed border-slate-200 rounded-xl">
          <Sparkles className="w-8 h-8 mb-2 text-slate-300" />
          <p className="text-sm">输入题目后点击"生成讲解"开始</p>
        </div>
      )}
    </div>
  )
}
